import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import { CheckCircle2, ExternalLink, Play, ThumbsDown, ThumbsUp, X, AlertTriangle, Info, Clock } from "lucide-react";
import { fetchTutorial, sendFeedback, setProgress, trackTutorialEvent } from "../../tutorials/api";
import { tutorialLabel } from "../../tutorials/labels";
import { useLang } from "../../i18n/LangContext";

const PROD_STATUS_KEY = {
  script_ready: "tut.production.script_ready",
  in_production: "tut.production.in_production",
  review: "tut.production.review",
  published: "tut.production.published",
};

const PROD_STATUS_COLOR = {
  script_ready: "bg-amber-100 text-amber-800 border-amber-300",
  in_production: "bg-blue-100 text-blue-800 border-blue-300",
  review: "bg-violet-100 text-violet-800 border-violet-300",
  published: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section className="space-y-2" data-testid={testid}>
      <h3 className="text-sm font-semibold flex items-center gap-2 text-slate-700 dark:text-slate-200">
        {Icon && <Icon className="w-4 h-4" />} {title}
      </h3>
      <div className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export default function TutorialViewer({ tutorialId, open, onClose }) {
  const { lang, t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);

  useEffect(() => {
    if (!open || !tutorialId) return;
    let alive = true;
    setLoading(true);
    setFeedbackDone(false);
    (async () => {
      try {
        const t2 = await fetchTutorial(tutorialId);
        if (!alive) return;
        setData(t2);
        // side effects — mark started + analytics.
        if (t2?.progress?.state !== "completed") {
          setProgress(tutorialId, "start").catch(() => {});
        }
        trackTutorialEvent(tutorialId, "open");
      } catch (_e) {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, tutorialId]);

  if (!tutorialId) return null;
  const label = tutorialLabel(lang, tutorialId);
  const script = data?.script || {};
  const status = data?.status || "script_ready";
  const isCompleted = data?.progress?.state === "completed";

  async function toggleComplete() {
    if (!data) return;
    const action = isCompleted ? "reset" : "complete";
    await setProgress(tutorialId, action).catch(() => {});
    if (action === "complete") trackTutorialEvent(tutorialId, "complete");
    // Refresh local state
    const t2 = await fetchTutorial(tutorialId).catch(() => null);
    if (t2) setData(t2);
  }

  async function feedback(helpful) {
    await sendFeedback(tutorialId, helpful).catch(() => {});
    setFeedbackDone(true);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="tutorial-viewer-modal">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle className="text-lg text-left">
                {label.title}
              </DialogTitle>
              <p className="text-xs text-slate-500 mt-1 text-left">
                {label.summary}
              </p>
            </div>
            <button
              onClick={onClose}
              data-testid="tutorial-viewer-close"
              className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-100 shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Badge variant="outline" className={`text-xs ${PROD_STATUS_COLOR[status]}`} data-testid="tutorial-status-badge">
              {t(PROD_STATUS_KEY[status])}
            </Badge>
            {data?.duration_seconds ? (
              <Badge variant="outline" className="text-xs flex items-center gap-1">
                <Clock className="w-3 h-3" /> {t("tut.card.duration", { sec: data.duration_seconds })}
              </Badge>
            ) : null}
            {isCompleted && (
              <Badge className="text-xs bg-emerald-600 text-white flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> {t("tut.status.completed")}
              </Badge>
            )}
          </div>
        </DialogHeader>

        {/* Player area — video or fallback */}
        <div className="mt-2">
          {data?.video_url ? (
            <div className="rounded-lg overflow-hidden bg-black aspect-video" data-testid="tutorial-video-container">
              <video
                controls
                preload="metadata"
                src={data.video_url}
                onPlay={() => trackTutorialEvent(tutorialId, "play")}
                onEnded={() => trackTutorialEvent(tutorialId, "complete")}
                className="w-full h-full">
                {t("tut.player.pending")}
              </video>
            </div>
          ) : (
            <div
              className="rounded-lg border border-dashed border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-4 flex items-start gap-3"
              data-testid="tutorial-video-placeholder">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900 dark:text-amber-100">
                {t("tut.player.pending")}
              </div>
            </div>
          )}
        </div>

        {loading && (
          <div className="text-sm text-slate-500 py-4">Loading…</div>
        )}

        {data && (
          <div className="space-y-5 mt-4">
            {script.objective && (
              <Section icon={Info} title={t("tut.section.objective")} testid="tut-section-objective">
                {script.objective}
              </Section>
            )}
            {script.when_to_use && (
              <Section icon={Info} title={t("tut.section.when")} testid="tut-section-when">
                {script.when_to_use}
              </Section>
            )}
            {data.prerequisites?.length > 0 && (
              <Section icon={Info} title={t("tut.section.prep")} testid="tut-section-prep">
                <ul className="list-disc pl-5 space-y-1">
                  {data.prerequisites.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </Section>
            )}
            {data.steps?.length > 0 && (
              <Section icon={Play} title={t("tut.section.steps")} testid="tut-section-steps">
                <ol className="list-decimal pl-5 space-y-1.5">
                  {data.steps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </Section>
            )}
            {script.result && (
              <Section icon={CheckCircle2} title={t("tut.section.result")} testid="tut-section-result">
                {script.result}
              </Section>
            )}
            {data.success_signals?.length > 0 && (
              <Section icon={CheckCircle2} title={t("tut.section.success")} testid="tut-section-success">
                <ul className="list-disc pl-5 space-y-1">
                  {data.success_signals.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </Section>
            )}
            {data.common_errors?.length > 0 && (
              <Section icon={AlertTriangle} title={t("tut.section.errors")} testid="tut-section-errors">
                <ul className="space-y-2">
                  {data.common_errors.map((e, i) => (
                    <li key={i} className="border-l-2 border-amber-400 pl-3">
                      <span className="font-medium">{e.when}</span>
                      <span className="text-slate-500"> — {e.fix}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}
            {script.next_action && (
              <Section icon={ExternalLink} title={t("tut.section.next")} testid="tut-section-next">
                {script.next_action}
              </Section>
            )}

            <Separator />

            {/* Action row */}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                onClick={toggleComplete}
                data-testid="tutorial-toggle-complete">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {isCompleted ? t("tut.mark.undo") : t("tut.mark.done")}
              </Button>
              {data.route && (
                <Link to={data.route} onClick={() => {
                  trackTutorialEvent(tutorialId, "cta_click", { to: data.route });
                  onClose();
                }}>
                  <Button data-testid="tutorial-go-to-tool">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t("tut.card.go")}
                  </Button>
                </Link>
              )}

              {/* Feedback */}
              <div className="ml-auto flex items-center gap-2 text-sm">
                {feedbackDone ? (
                  <span className="text-emerald-600" data-testid="tutorial-feedback-thanks">
                    {t("tut.feedback.thanks")}
                  </span>
                ) : (
                  <>
                    <span className="text-slate-500">{t("tut.feedback.q")}</span>
                    <button
                      onClick={() => feedback(true)}
                      className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600"
                      data-testid="tutorial-feedback-yes"
                      aria-label={t("tut.feedback.yes")}>
                      <ThumbsUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => feedback(false)}
                      className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                      data-testid="tutorial-feedback-no"
                      aria-label={t("tut.feedback.no")}>
                      <ThumbsDown className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
